import { useEffect } from 'react';

import { Card, CardBody, Form, LabeledSwitch } from '@/_components';
import {
  BulkDeleteError,
  ContainerLayout,
  DataTable,
  DataTableToolbar,
  FiltersContextWithAccordion,
  FilterTrigger,
  InfiniteScrollStatus,
  NewButton,
  NewButtonUI,
  StickyPageHeader,
} from '@/_containers';
import { usePermissions } from '@/_providers';

import { InvoiceBottomPanels } from './invoice-bottom-panels';
import { InvoicesSplitView } from './invoices-split-view';

import { invoiceBatchFilterRenderer } from '../../main-invoice-batches/components';
import { useMainBatchInvoicesPage } from '../../main-invoice-batches/use-main-batch-invoices-page';
import { useInvoiceBatchFilterFormDefinition } from '../../shared/filters/use-invoice-batch-filter-form-definition';
import { getBatchInvoiceListRowClassName } from '../../shared/invoice-void-row-style';
import { useInvoiceDigitalPdfShortcut } from '../hooks/use-invoice-digital-pdf-shortcut';
import { useInvoiceEmailShortcut } from '../hooks/use-invoice-email-shortcut';
import type { PageShellState } from '../use-page';

type InvoiceBatchesViewProps = {
  page: PageShellState;
};

export function InvoiceBatchesView({ page }: InvoiceBatchesViewProps) {
  const {
    batchFilters,
    isEnter,
    enterState,
    activeBatchInvoiceId,
    setActiveBatchInvoiceId,
    isBatchMode,
    batchMode,
    handleKeyDown,
    handleBatchInvoiceRowClick,
    handleBatchInvoiceRowFocus,
    handleOpenArPayments,
    paymentCreation,
    sendingInvoiceId,
    beginSendingInvoice,
    endSendingInvoice,
    openEmailModal,
  } = page;
  const { currentModelPermissions } = usePermissions();
  const batchForm = useInvoiceBatchFilterFormDefinition();
  const mainBatchInvoicesTable = useMainBatchInvoicesPage(batchFilters, {
    openEmailModal,
    beginSendingInvoice,
    endSendingInvoice,
  });
  const hasBatchInvoices = mainBatchInvoicesTable.allBatches.length > 0;
  const batchListInvoices = Object.values(mainBatchInvoicesTable.loadedBatchInvoices).flat();

  useInvoiceDigitalPdfShortcut({
    activeInvoiceId: activeBatchInvoiceId,
    invoices: batchListInvoices,
  });

  useInvoiceEmailShortcut({
    activeInvoiceId: activeBatchInvoiceId,
    invoices: batchListInvoices,
    sendingInvoiceId,
    onOpenEmailModal: openEmailModal,
  });

  useEffect(() => {
    mainBatchInvoicesTable.table.toggleAllRowsExpanded(Boolean(batchFilters.searching.search_str));
  }, [batchFilters.searching.search_str, mainBatchInvoicesTable.table]);

  useEffect(() => {
    if (!isEnter) {
      return;
    }
    enterState.setFalse();
    mainBatchInvoicesTable.handleOnSingleValueResult();
  }, [enterState, isEnter, mainBatchInvoicesTable]);

  useEffect(() => {
    if (!activeBatchInvoiceId) {
      return;
    }

    const isCurrentBatchInvoiceInTable = Object.values(mainBatchInvoicesTable.loadedBatchInvoices)
      .flat()
      .some((invoice) => String(invoice.id) === activeBatchInvoiceId);

    if (!isCurrentBatchInvoiceInTable) {
      setActiveBatchInvoiceId(null);
    }
  }, [activeBatchInvoiceId, mainBatchInvoicesTable.loadedBatchInvoices, setActiveBatchInvoiceId]);

  return (
    <Form {...batchForm} isSubmitting={false}>
      <FiltersContextWithAccordion
        title="Invoice Batches"
        advFilters={batchFilters.advFiltersState}
        handleSubmitAdvFilters={batchFilters.handleSubmitAdvFilters}
        handleClearAllAdvFilters={batchFilters.handleClearAllAdvFilters}
        filterRenderer={invoiceBatchFilterRenderer}
      >
        <ContainerLayout>
          <StickyPageHeader title="Invoice Batches" recordCount={mainBatchInvoicesTable.total}>
            {currentModelPermissions?.create && (
              <div className="flex items-center gap-2">
                {!!paymentCreation && <NewButtonUI onClick={handleOpenArPayments} title="AR Payment" osKey="P" />}
                <NewButton to="/invoices/new" title="New Invoice Batch" />
              </div>
            )}
          </StickyPageHeader>

          <BulkDeleteError hasIssues={mainBatchInvoicesTable.hasIssues} name="Invoice" pluralName="Invoices" />

          <article className="mt-2 px-6">
            <Card>
              <CardBody className="space-y-4">
                <DataTableToolbar
                  table={mainBatchInvoicesTable.table}
                  searching={batchFilters.searching}
                  isDeleting={mainBatchInvoicesTable.isBulkDeleting}
                  deleteCallback={currentModelPermissions?.delete ? mainBatchInvoicesTable.handleBulkDelete : undefined}
                  onKeyDown={handleKeyDown}
                  rightSlot={<FilterTrigger advFilters={batchFilters.advFiltersState} />}
                >
                  <div className="flex flex-1 justify-end">
                    <LabeledSwitch
                      id="show"
                      tabIndex={-1}
                      checked={isBatchMode}
                      onCheckedChange={batchMode.toggle}
                      label="Invoice Batches"
                      labelPlacement="before"
                    />
                  </div>
                </DataTableToolbar>

                {hasBatchInvoices ? (
                  <InvoicesSplitView
                    topContent={
                      <DataTable
                        table={mainBatchInvoicesTable.table}
                        tabIndex={2}
                        virtualized
                        infiniteScroll={{
                          fetchNextPage: mainBatchInvoicesTable.fetchNextPage,
                          hasNextPage: mainBatchInvoicesTable.hasNextPage,
                          isFetchingNextPage: mainBatchInvoicesTable.isFetchingNextPage,
                        }}
                        onFocusEnter={mainBatchInvoicesTable.handleFocusEnter}
                        onRowClick={handleBatchInvoiceRowClick}
                        onRowFocus={handleBatchInvoiceRowFocus}
                        isRowActive={(row) => !row.isBatch && String(row.id) === activeBatchInvoiceId}
                        getRowClassName={(row) => getBatchInvoiceListRowClassName(row.original, sendingInvoiceId)}
                        containerClassName="h-full overflow-auto rounded-b-none"
                        tableHeaderClassName="sticky top-0 z-10 bg-white dark:bg-gray-950"
                      />
                    }
                    bottomContent={
                      <InvoiceBottomPanels activeInvoiceId={activeBatchInvoiceId} listInvoices={batchListInvoices} />
                    }
                  />
                ) : (
                  <DataTable
                    table={mainBatchInvoicesTable.table}
                    tabIndex={2}
                    onFocusEnter={mainBatchInvoicesTable.handleFocusEnter}
                    onRowClick={handleBatchInvoiceRowClick}
                    onRowFocus={handleBatchInvoiceRowFocus}
                    isRowActive={(row) => !row.isBatch && String(row.id) === activeBatchInvoiceId}
                    getRowClassName={(row) => getBatchInvoiceListRowClassName(row.original, sendingInvoiceId)}
                  />
                )}

                <InfiniteScrollStatus
                  loadedCount={mainBatchInvoicesTable.allBatches.length}
                  total={mainBatchInvoicesTable.total}
                  isFetchingNextPage={mainBatchInvoicesTable.isFetchingNextPage}
                  hasNextPage={mainBatchInvoicesTable.hasNextPage}
                />
              </CardBody>
            </Card>
          </article>
        </ContainerLayout>
      </FiltersContextWithAccordion>
    </Form>
  );
}
